import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  Clock,
  Download,
  Trophy,
  CheckCircle2,
  Timer,
  BarChart3,
  Building2,
  Users,
  MessageSquare,
  Bot,
  Heart,
  Copy,
  BookOpen,
  Layers,
  Film,
  Trash2,
  HelpCircle,
  Zap,
  Star,
  CalendarIcon,
  X,
} from 'lucide-react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts'
import { CoursesManagement } from '@/components/admin/CoursesManagement'
import { ModulesManagement } from '@/components/admin/ModulesManagement'
import { EpisodesManagement } from '@/components/admin/EpisodesManagement'
import { TrashManagement } from '@/components/admin/TrashManagement'
import { UsersManagement } from '@/components/admin/UsersManagement'
import { MyCompanySettings } from '@/components/admin/MyCompanySettings'
import { useAuth } from '@/hooks/use-auth'
import { QuizManagement } from '@/components/admin/QuizManagement'
import DropsManagement from '@/components/admin/DropsManagement'
import {
  useMetricsOverview,
  useUserRanking,
  useAverageCourseProgress,
  useQuizAnalytics,
  useCourseFeedbacks,
} from '@/services/admin/metrics.service'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAdminCourses } from '@/services/admin/courses.service'
import {
  useWorkbookRankings,
  type WorkbookRankingEpisode,
} from '@/services/admin/workbook-analytics.service'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { Button } from '@/components/ui/button'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

// Mock data for engagement metrics
const moduleCompletionData = [
  { module: 'Módulo 1', started: 500, completed: 420, rate: 84 },
  { module: 'Módulo 2', started: 420, completed: 340, rate: 81 },
  { module: 'Módulo 3', started: 340, completed: 255, rate: 75 },
  { module: 'Módulo 4', started: 255, completed: 178, rate: 70 },
  { module: 'Módulo 5', started: 178, completed: 107, rate: 60 },
  { module: 'Módulo 6', started: 107, completed: 64, rate: 60 },
]

// Sym! Hub overview data
const symHubOverview = {
  totalPrompts: 847,
  totalAgents: 234,
  totalLikes: 3456,
  totalCopies: 1892,
}

const Admin = () => {
  const [selectedCourseId, setSelectedCourseId] = useState<number | undefined>(undefined)
  const { data: adminCourses } = useAdminCourses()
  const { user } = useAuth()
  // Tab "Empresa" só aparece se admin está vinculado a uma empresa.
  // Super_admin sem companyId não vê (usa CompaniesManagement no /super-admin).
  const hasCompany = !!user?.companyId
  const { data: metrics, isLoading: metricsLoading } = useMetricsOverview(selectedCourseId)
  const { data: userRanking, isLoading: rankingLoading } = useUserRanking(selectedCourseId)
  const { data: avgProgress, isLoading: avgProgressLoading } =
    useAverageCourseProgress(selectedCourseId)
  const { data: workbookRankings, isLoading: workbookLoading } = useWorkbookRankings(
    'episodes',
    'all',
    20
  )
  const { data: quizAnalytics, isLoading: quizAnalyticsLoading } =
    useQuizAnalytics(selectedCourseId)

  // Feedback filters (independent from the global course filter)
  const [feedbackCourseId, setFeedbackCourseId] = useState<number | undefined>(undefined)
  const [feedbackDateFrom, setFeedbackDateFrom] = useState<Date | undefined>(undefined)
  const [feedbackDateTo, setFeedbackDateTo] = useState<Date | undefined>(undefined)
  const { data: feedbackData, isLoading: feedbackLoading } = useCourseFeedbacks({
    courseId: feedbackCourseId,
    dateFrom: feedbackDateFrom ? format(feedbackDateFrom, 'yyyy-MM-dd') : undefined,
    dateTo: feedbackDateTo ? format(feedbackDateTo, 'yyyy-MM-dd') : undefined,
  })

  // Use dados reais se disponíveis, senão usa mock data como fallback
  const symHubOverview = metrics?.symHub || {
    totalPrompts: 0,
    totalAgents: 0,
    totalLikes: 0,
    totalCopies: 0,
  }

  const moduleCompletionData = metrics?.moduleCompletion || []

  const completionTimeData = metrics?.completionTime || {
    averageDays: 0,
    fastestDays: 0,
    slowestDays: 0,
    totalCompleted: 0,
  }

  // Processa dados de workbook (views + downloads) da API.
  // useWorkbookRankings('episodes', ...) garante o tipo Episode no array,
  // mas o hook retorna union — cast explícito pra evitar narrow manual.
  const episodeRankings = workbookRankings as WorkbookRankingEpisode[] | undefined
  const workbookStats = {
    total: episodeRankings?.reduce((sum, item) => sum + item.totalActions, 0) || 0,
    byEpisode:
      episodeRankings?.map((item) => ({
        episode: item.episode?.title || `Episódio ${item.episodeId}`,
        interactions: item.totalActions,
      })) || [],
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent/5 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      <main className="container py-8 relative z-10">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar para o início
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-primary" />
            Dashboard Admin
          </h1>
          <p className="text-muted-foreground mt-2">
            Gerenciamento de cursos, módulos, episódios e métricas
          </p>
        </motion.div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="glass-card border-border/50 p-1">
            {/* <TabsTrigger value="courses" className="gap-2">
              <BookOpen className="w-4 h-4" />
              Cursos
            </TabsTrigger>
            <TabsTrigger value="modules" className="gap-2">
              <Layers className="w-4 h-4" />
              Módulos
            </TabsTrigger>
            <TabsTrigger value="episodes" className="gap-2">
              <Film className="w-4 h-4" />
              Episódios
            </TabsTrigger>
            <TabsTrigger value="quizzes" className="gap-2">
              <HelpCircle className="w-4 h-4" />
              Quizzes
            </TabsTrigger>
            <TabsTrigger value="drops" className="gap-2">
              <Zap className="w-4 h-4" />
              Drops
            </TabsTrigger> */}
            <TabsTrigger value="users" className="gap-2">
              <Users className="w-4 h-4" />
              Usuários
            </TabsTrigger>
            {hasCompany && (
              <TabsTrigger value="company" className="gap-2">
                <Building2 className="w-4 h-4" />
                Empresa
              </TabsTrigger>
            )}
            {/* <TabsTrigger value="trash" className="gap-2">
              <Trash2 className="w-4 h-4" />
              Lixeira
            </TabsTrigger> */}
            <TabsTrigger value="metrics" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Métricas
            </TabsTrigger>
          </TabsList>

          {/* Company Tab (admin com companyId) */}
          {hasCompany && (
            <TabsContent value="company">
              <MyCompanySettings />
            </TabsContent>
          )}

          {/* Courses Tab */}
          <TabsContent value="courses">
            <CoursesManagement />
          </TabsContent>

          {/* Modules Tab */}
          <TabsContent value="modules">
            <ModulesManagement />
          </TabsContent>

          {/* Episodes Tab */}
          <TabsContent value="episodes">
            <EpisodesManagement />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <UsersManagement />
          </TabsContent>

          {/* Trash Tab */}
          <TabsContent value="trash">
            <TrashManagement />
          </TabsContent>

          {/* Quizzes Tab */}
          <TabsContent value="quizzes">
            <QuizManagement />
          </TabsContent>

          {/* Drops Tab */}
          <TabsContent value="drops">
            <DropsManagement />
          </TabsContent>

          {/* Metrics Tab */}
          <TabsContent value="metrics" className="space-y-6">
            {/* Content Metrics Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mb-12"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  Métricas de Conteúdo
                </h2>
                <Select
                  value={selectedCourseId?.toString() || 'all'}
                  onValueChange={(value) =>
                    setSelectedCourseId(value === 'all' ? undefined : Number(value))
                  }
                >
                  <SelectTrigger className="w-[250px]">
                    <SelectValue placeholder="Todos os cursos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os cursos</SelectItem>
                    {adminCourses?.map((course) => (
                      <SelectItem key={course.id} value={course.id.toString()}>
                        {course.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {/* User Ranking */}
                <Card className="glass-card border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Trophy className="w-5 h-5 text-primary" />
                      Ranking de Usuários
                    </CardTitle>
                    <CardDescription>Classificação por aulas completadas</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {rankingLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Clock className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : !userRanking || userRanking.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>Nenhum dado de ranking disponível ainda.</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                        {userRanking.map((user, index) => (
                          <div
                            key={user.userId}
                            className="flex items-center gap-3 p-2 rounded-lg bg-muted/20"
                          >
                            <span
                              className={`text-sm font-bold w-6 text-center ${index < 3 ? 'text-primary' : 'text-muted-foreground'}`}
                            >
                              {index + 1}º
                            </span>
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={user.userAvatar} />
                              <AvatarFallback className="text-xs bg-primary/10 text-primary">
                                {user.userName?.substring(0, 2).toUpperCase() || '??'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {user.userName}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {user.userEmail}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-primary">
                                {user.totalCompleted}
                              </p>
                              <p className="text-xs text-muted-foreground">completadas</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Average Course Progress */}
                <Card className="glass-card border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <BarChart3 className="w-5 h-5 text-primary" />
                      Visão Geral
                    </CardTitle>
                    <CardDescription>
                      Resumo da plataforma e progresso médio dos cursos
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col items-center justify-center py-8">
                    {avgProgressLoading ? (
                      <Clock className="h-8 w-8 animate-spin text-primary" />
                    ) : (
                      <>
                        <div className="relative w-32 h-32 mb-4">
                          <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                            <path
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              fill="none"
                              stroke="hsl(var(--muted))"
                              strokeWidth="3"
                            />
                            <path
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              fill="none"
                              stroke="hsl(var(--primary))"
                              strokeWidth="3"
                              strokeDasharray={`${avgProgress?.averagePercent || 0}, 100`}
                              strokeLinecap="round"
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-3xl font-bold text-primary">
                              {avgProgress?.averagePercent ?? 0}%
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-3 w-full">
                          <div className="bg-muted/30 rounded-lg p-3 text-center">
                            <p className="text-lg font-semibold text-primary">
                              {avgProgress?.totalRegisteredUsers ?? 0}
                            </p>
                            <p className="text-xs text-muted-foreground">Usuários cadastrados</p>
                          </div>
                          <div className="bg-muted/30 rounded-lg p-3 text-center">
                            <p className="text-lg font-semibold text-primary">
                              {avgProgress?.totalUsers ?? 0}
                            </p>
                            <p className="text-xs text-muted-foreground">Usuários matriculados</p>
                          </div>
                          <div className="bg-muted/30 rounded-lg p-3 text-center">
                            <p className="text-lg font-semibold text-primary">
                              {avgProgress?.totalCourses ?? 0}
                            </p>
                            <p className="text-xs text-muted-foreground">Cursos ativos</p>
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Workbook Interactions (Views + Downloads) */}
                <Card className="glass-card border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Download className="w-5 h-5 text-primary" />
                      Interações com Workbooks
                    </CardTitle>
                    <CardDescription>
                      Total:{' '}
                      <span className="text-primary font-semibold">{workbookStats.total}</span>{' '}
                      visualizações e downloads
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {workbookLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Clock className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : workbookStats.byEpisode.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>Nenhuma interação com workbook registrada ainda.</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                        {workbookStats.byEpisode.map((item, index) => {
                          const maxInteractions = workbookStats.byEpisode[0]?.interactions || 1
                          return (
                            <div key={`${item.episode}-${index}`} className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground truncate max-w-[200px]">
                                  {item.episode}
                                </span>
                                <span className="text-foreground font-medium">
                                  {item.interactions}
                                </span>
                              </div>
                              <Progress
                                value={(item.interactions / maxInteractions) * 100}
                                className="h-2"
                              />
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Module Completion Rate */}
                <Card className="glass-card border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <CheckCircle2 className="w-5 h-5 text-primary" />
                      Taxa de Conclusão por Módulo
                    </CardTitle>
                    <CardDescription>
                      Usuários que começaram vs. terminaram cada módulo
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {metricsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Clock className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : moduleCompletionData.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>Nenhum dado de conclusão disponível ainda.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {moduleCompletionData.map((module) => (
                          <div key={module.moduleId} className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-foreground font-medium">
                                {module.moduleName}
                              </span>
                              <span className="text-muted-foreground">
                                {module.completed}/{module.started} usuários ({module.rate}%)
                              </span>
                            </div>
                            <div className="flex gap-2 items-center">
                              <Progress value={module.rate} className="h-3 flex-1" />
                              <span className="text-primary font-semibold text-sm w-12 text-right">
                                {module.rate}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Average Time to Complete */}
                <Card className="glass-card border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Timer className="w-5 h-5 text-primary" />
                      Tempo Médio de Conclusão
                    </CardTitle>
                    <CardDescription>Curso completo</CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col items-center justify-center py-8">
                    {metricsLoading ? (
                      <Clock className="h-8 w-8 animate-spin text-primary" />
                    ) : (
                      <>
                        <div className="text-5xl font-bold text-primary mb-2">
                          {completionTimeData.averageDays || '-'}
                        </div>
                        <div className="text-xl text-muted-foreground">dias</div>
                        <div className="mt-4 text-sm text-muted-foreground text-center">
                          <p>Média baseada em {completionTimeData.totalCompleted} usuários</p>
                          <p>que completaram o curso</p>
                        </div>
                        <div className="mt-4 grid grid-cols-2 gap-4 w-full">
                          <div className="bg-muted/30 rounded-lg p-3 text-center">
                            <p className="text-lg font-semibold text-primary">
                              {completionTimeData.fastestDays || '-'}
                            </p>
                            <p className="text-xs text-muted-foreground">Mais rápido (dias)</p>
                          </div>
                          <div className="bg-muted/30 rounded-lg p-3 text-center">
                            <p className="text-lg font-semibold text-primary">
                              {completionTimeData.slowestDays || '-'}
                            </p>
                            <p className="text-xs text-muted-foreground">Mais lento (dias)</p>
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Quiz: Nota Média por Módulo */}
                <Card className="glass-card border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Trophy className="w-5 h-5 text-primary" />
                      Nota Média por Módulo (Quiz)
                    </CardTitle>
                    <CardDescription>Score médio dos quizzes agrupado por módulo</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {quizAnalyticsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Clock className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : !quizAnalytics?.averageScoreByModule.length ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>Nenhum dado disponível ainda.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {quizAnalytics.averageScoreByModule.map((mod) => (
                          <div key={mod.moduleId} className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-foreground font-medium">{mod.moduleName}</span>
                              <span className="text-muted-foreground">
                                {mod.averageScore}% ({mod.totalAttempts} tentativa
                                {mod.totalAttempts !== 1 ? 's' : ''})
                              </span>
                            </div>
                            <div className="flex gap-2 items-center">
                              <Progress value={mod.averageScore} className="h-3 flex-1" />
                              <span className="text-primary font-semibold text-sm w-12 text-right">
                                {mod.averageScore}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Quiz: Taxa de Conclusão de Quizzes */}
                <Card className="glass-card border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <CheckCircle2 className="w-5 h-5 text-primary" />
                      Taxa de Conclusão de Quizzes
                    </CardTitle>
                    <CardDescription>
                      % de alunos matriculados que responderam cada quiz
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {quizAnalyticsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Clock className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : !quizAnalytics?.completionByModuleAndEpisode.length ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>Nenhum dado disponível ainda.</p>
                      </div>
                    ) : (
                      <div className="space-y-5 max-h-[500px] overflow-y-auto pr-1">
                        {quizAnalytics.completionByModuleAndEpisode.map((mod) => (
                          <div key={mod.moduleId}>
                            <p className="text-sm font-semibold text-foreground mb-2">
                              {mod.moduleName}
                            </p>
                            <div className="space-y-3 pl-2">
                              {mod.episodes.map((ep) => (
                                <div key={ep.episodeId} className="space-y-1">
                                  <div className="flex justify-between text-sm">
                                    <span
                                      className="text-muted-foreground truncate max-w-[200px]"
                                      title={ep.episodeTitle}
                                    >
                                      {ep.quizTitle}
                                    </span>
                                    <span className="text-foreground font-medium whitespace-nowrap ml-2">
                                      {ep.totalAttempted} de {ep.totalEnrolled} ({ep.completionRate}
                                      %)
                                    </span>
                                  </div>
                                  <Progress value={ep.completionRate} className="h-2" />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Quiz: Tempo Médio de Resposta */}
                <Card className="glass-card border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Timer className="w-5 h-5 text-primary" />
                      Tempo Médio de Resposta
                    </CardTitle>
                    <CardDescription>Tempo médio gasto por questão de quiz</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {quizAnalyticsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Clock className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : !quizAnalytics?.avgResponseTimeByQuestion.length ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>
                          Sem dados de tempo ainda. Os tempos serão coletados a partir das próximas
                          respostas.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                        {quizAnalytics.avgResponseTimeByQuestion.map((q) => {
                          const seconds = Math.round(q.avgTimeMs / 1000)
                          const formatted =
                            seconds >= 60
                              ? `${Math.floor(seconds / 60)}m ${seconds % 60}s`
                              : `${seconds}s`
                          return (
                            <div
                              key={q.questionId}
                              className="p-3 rounded-lg bg-muted/20 space-y-1"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm text-foreground font-medium leading-snug flex-1">
                                  {q.questionText}
                                </p>
                                <span className="text-primary font-bold text-sm whitespace-nowrap">
                                  {formatted}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                  {q.quizTitle}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {q.totalResponses} resposta{q.totalResponses !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Quiz: Tentativas por Quiz */}
                <Card className="glass-card border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Users className="w-5 h-5 text-primary" />
                      Tentativas por Quiz
                    </CardTitle>
                    <CardDescription>
                      Quantidade de alunos que responderam cada quiz
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {quizAnalyticsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Clock className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : !quizAnalytics?.attemptsByQuiz.length ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>Nenhum dado disponível ainda.</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                        {quizAnalytics.attemptsByQuiz.map((quiz) => (
                          <div key={quiz.quizId} className="p-3 rounded-lg bg-muted/20">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-sm font-medium text-foreground">
                                {quiz.quizTitle}
                              </p>
                              <span className="text-primary font-bold text-sm">
                                {quiz.totalAttempts} tentativa{quiz.totalAttempts !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">
                              {quiz.moduleName} &bull; {quiz.episodeTitle}
                            </p>
                            <div className="grid grid-cols-3 gap-2">
                              <div className="bg-muted/30 rounded p-2 text-center">
                                <p className="text-xs text-muted-foreground">Média</p>
                                <p className="text-sm font-semibold text-primary">
                                  {quiz.averageScore}%
                                </p>
                              </div>
                              <div className="bg-muted/30 rounded p-2 text-center">
                                <p className="text-xs text-muted-foreground">Maior</p>
                                <p className="text-sm font-semibold text-green-400">
                                  {quiz.highestScore}%
                                </p>
                              </div>
                              <div className="bg-muted/30 rounded p-2 text-center">
                                <p className="text-xs text-muted-foreground">Menor</p>
                                <p className="text-sm font-semibold text-red-400">
                                  {quiz.lowestScore}%
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Feedbacks dos Cursos — full width */}
                <Card className="glass-card border-border/50 md:col-span-2">
                  <CardHeader>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
                      <div className="space-y-1.5">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Star className="w-5 h-5 text-primary" />
                          Avaliações dos Cursos
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2">
                          {feedbackData ? (
                            <>
                              <span className="text-primary font-semibold text-lg">
                                {feedbackData.averageRating}
                              </span>
                              <span className="flex">
                                {[1, 2, 3, 4, 5].map((s) => (
                                  <Star
                                    key={s}
                                    className={`w-4 h-4 ${s <= Math.round(feedbackData.averageRating) ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground/30'}`}
                                  />
                                ))}
                              </span>
                              <span className="text-muted-foreground">
                                ({feedbackData.totalFeedbacks} avaliač
                                {feedbackData.totalFeedbacks !== 1 ? 'ões' : 'ão'})
                              </span>
                            </>
                          ) : (
                            <span>Carregando...</span>
                          )}
                        </CardDescription>
                        {/* Filters */}
                        <div className="flex flex-wrap gap-2 pt-3">
                          <Select
                            value={feedbackCourseId?.toString() || 'all'}
                            onValueChange={(v) =>
                              setFeedbackCourseId(v === 'all' ? undefined : Number(v))
                            }
                          >
                            <SelectTrigger className="w-[200px] h-9 text-xs">
                              <SelectValue placeholder="Todos os cursos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos os cursos</SelectItem>
                              {adminCourses?.map((course) => (
                                <SelectItem key={course.id} value={course.id.toString()}>
                                  {course.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5">
                                <CalendarIcon className="w-3.5 h-3.5" />
                                {feedbackDateFrom ? format(feedbackDateFrom, 'dd/MM/yyyy') : 'De'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={feedbackDateFrom}
                                onSelect={setFeedbackDateFrom}
                                locale={ptBR}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>

                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5">
                                <CalendarIcon className="w-3.5 h-3.5" />
                                {feedbackDateTo ? format(feedbackDateTo, 'dd/MM/yyyy') : 'Até'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={feedbackDateTo}
                                onSelect={setFeedbackDateTo}
                                locale={ptBR}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>

                          {(feedbackDateFrom || feedbackDateTo || feedbackCourseId) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-9 text-xs gap-1 text-muted-foreground hover:text-foreground"
                              onClick={() => {
                                setFeedbackCourseId(undefined)
                                setFeedbackDateFrom(undefined)
                                setFeedbackDateTo(undefined)
                              }}
                            >
                              <X className="w-3.5 h-3.5" />
                              Limpar
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        {[5, 4, 3, 2, 1].map((star) => {
                          const count = feedbackData?.distribution[star] || 0
                          const total = feedbackData?.totalFeedbacks || 0
                          const pct = total > 0 ? (count / total) * 100 : 0
                          const colors: Record<number, string> = {
                            5: 'bg-green-500',
                            4: 'bg-green-400',
                            3: 'bg-yellow-400',
                            2: 'bg-orange-400',
                            1: 'bg-red-400',
                          }
                          return (
                            <div key={star} className="flex items-center gap-3 text-xs">
                              <span className="flex items-center gap-1 w-8 justify-end text-muted-foreground tabular-nums">
                                {star}
                                <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                              </span>
                              <div className="flex-1 h-2 bg-muted/30 rounded-full overflow-hidden">
                                <div
                                  className={`${colors[star]} h-full transition-all`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="w-8 text-right text-muted-foreground tabular-nums">
                                {count}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {feedbackLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Clock className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    ) : !feedbackData?.feedbacks.length ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>Nenhum feedback encontrado para os filtros selecionados.</p>
                      </div>
                    ) : (
                      <>
                        {/* Feedback list */}
                        <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                          {feedbackData.feedbacks.map((fb) => (
                            <div key={fb.id} className="p-4 rounded-xl bg-muted/20 space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Avatar className="h-7 w-7">
                                    <AvatarImage src={fb.userAvatar || undefined} />
                                    <AvatarFallback className="text-xs bg-primary/10 text-primary">
                                      {fb.userName?.substring(0, 2).toUpperCase() || '??'}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <p className="text-sm font-medium text-foreground">
                                      {fb.userName}
                                    </p>
                                    <p className="text-xs text-muted-foreground">{fb.courseName}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  {[1, 2, 3, 4, 5].map((s) => (
                                    <Star
                                      key={s}
                                      className={`w-3.5 h-3.5 ${s <= fb.rating ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground/30'}`}
                                    />
                                  ))}
                                </div>
                              </div>
                              <p className="text-sm text-muted-foreground leading-relaxed">
                                {fb.comment}
                              </p>
                              <p className="text-xs text-muted-foreground/60">
                                {new Date(fb.createdAt).toLocaleDateString('pt-BR', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                })}
                              </p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  )
}

export default Admin
